import React, { Fragment, useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { fetchData, postData } from '../../../apiService';
import IconButton from '@mui/material/IconButton';
import DeleteIcon from '@mui/icons-material/Delete';
import { useAuth } from '../../../AuthContext';
import { tokenIsExpired } from '../../../authUtils';
import { Link } from 'react-router-dom';
import Select from 'react-select';
import AddIcon from '@mui/icons-material/Add';
import DoneIcon from '@mui/icons-material/Done';
import CloseIcon from '@mui/icons-material/Close';
import CopyrightIcon from '@mui/icons-material/Copyright';
import CircularProgress from '@mui/material/CircularProgress';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import generateInterCityPDF from './Bill';
import PrintIcon from '@mui/icons-material/Print';
import '../shared.css';

const InterCityStockTransferCreate = () => {
    const history = useHistory();
    const [successMessage, setSuccessMessage] = useState('');
    const [errorMessage, setErrorMessage]     = useState('');
    const [errorMessageModel, setErrorMessageModel] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [confirmRemove, setConfirmRemove] = useState(null);
    const [isValid, setIsValid] = useState(true);

    const { isAuthenticated, companyId, branchId, userId, permissions, token, logout, branchstate } = useAuth();

    const [selectedSalesMan, setSelectedSalesMan]             = useState('');
    const [dropdownSalesManOptions, setDropdownSalesManOptions] = useState([]);
    const [selectedToBranch, setSelectedToBranch]             = useState('');
    const [dropdownBranchOptions, setDropdownBranchOptions]   = useState([]);
    const [branchData, setBranchData]                         = useState([]);

    const [formData, setFormData]           = useState({});
    const [invoiceDetails, setInvoiceDetails] = useState([]);

    // Fixed total discount target for this module
    const TOTAL_DISC_PCT = 52;

    const emptyModal = {
        ItemId: '', ItemName: '', Quantity: '1', Rate: '',
        Amount: '', DiscountPercentage: '0', DiscountAmount: '0',
        AdditionalDiscountPercentage: '0', AdditionalDiscountAmount: '0',
        RateAfterDiscount: '', HSNCode: '', GSTRate: '',
        TaxableAmount: '', CGSTAmount: '', SGSTAmount: '', IGSTAmount: '',
        TotalAmount: '', balanceStock: 0,
    };
    const [modalData, setModalData]       = useState(emptyModal);
    const [itemsOptions, setItemsOptions] = useState([]);
    const [itemDetails, setItemDetails]   = useState([]);
    const [selectedItem, setSelectedItem] = useState(null);

    useEffect(() => {
        if (isAuthenticated && tokenIsExpired(token)) { logout(); return; }
    }, [isAuthenticated, companyId, branchId, token, logout]);

    useEffect(() => {
        fetchDropdownSalesManOptions();
        fetchBranchOptions();
        fetchMaxBillNo();
        fetchItems();
    }, []);

    const fetchMaxBillNo = () => {
        fetchData('InterCityStockTransferApi', `/MaxBillno/${companyId}/${branchId}`, token)
            .then(r => {
                const d = new Date();
                const today = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
                setFormData(prev => ({
                    ...prev,
                    BillNumber: r.response_data?.BillNumber,
                    BillPrefix: r.response_data?.BillPrefix,
                    BillDate: today,
                    FromBranchId: branchId,
                    CompanyId: companyId,
                    CreatedBy: userId,
                    IsActive: true,
                    DiscountPercentage: 0, TotalAmount: 0, DiscountAmount: 0,
                    TaxableAmount: 0, CGSTAmount: 0, SGSTAmount: 0, IGSTAmount: 0,
                    RoundOff: 0, GrandTotal: 0, CashAmount: 0, CardAmount: 0,
                    UPIAmount: 0, BalanceAmount: 0, TotalQuantity: 0,
                }));
            })
            .catch(console.error);
    };

    const fetchDropdownSalesManOptions = () => {
        fetchData('InterCityStockTransferApi', `/SalesMan/${companyId}/${branchId}`, token)
            .then(r => setDropdownSalesManOptions(r.response_data || []))
            .catch(console.error);
    };

    const fetchBranchOptions = () => {
        fetchData('InterCityStockTransferApi', `/Branch/${companyId}/${branchId}`, token)
            .then(r => { setDropdownBranchOptions(r.response_data || []); setBranchData(r.response_data || []); })
            .catch(console.error);
    };

    const fetchItems = () => {
        fetchData('InterCityStockTransferApi', `/Item/${companyId}/${branchId}`, token)
            .then(r => {
                if (r.responseCode === '1111') {
                    setItemsOptions(r.response_data.map(i => ({ value: i.Id, label: `${i.Code}-${i.Name}` })));
                    setItemDetails(r.response_data);
                }
            })
            .catch(console.error);
    };

    useEffect(() => {
        if (selectedToBranch) {
            const b = branchData.find(x => x.Id == selectedToBranch);
            if (b) setFormData(p => ({
                ...p,
                ToBranchId:      selectedToBranch,
                CustomerName:    b.Name        || '',
                CustomerAddress: b.AddressLine || '',
                GSTNumber:       b.GSTNumber   || '',
                ContactNumber:   b.ContactNumber1 || '',
            }));
        }
    }, [selectedToBranch, branchData]);

    /* ── GST calculation ── */
    const calcGST = (qty, rate, rateAfterDisc, gstrate, gstNum) => {
        const taxable = (qty * rateAfterDisc / (1 + gstrate / 100)).toFixed(2);
        let cgst = 0, sgst = 0, igst = 0;
        const prefix = (gstNum || '').toString().substring(0, 2);
        if (!gstNum) {
            cgst = ((taxable * (gstrate / 2)) / 100).toFixed(2);
            sgst = ((taxable * (gstrate / 2)) / 100).toFixed(2);
        } else if (prefix !== branchstate) {
            igst = ((taxable * gstrate) / 100).toFixed(2);
        } else {
            cgst = ((taxable * (gstrate / 2)) / 100).toFixed(2);
            sgst = ((taxable * (gstrate / 2)) / 100).toFixed(2);
        }
        const total = Math.round((parseFloat(taxable) + parseFloat(cgst) + parseFloat(sgst) + parseFloat(igst)) * 100) / 100;
        return { taxable, cgst, sgst, igst, total };
    };

    /* ── Item select in bar ── */
    const handleItemSelect = (option) => {
        setSelectedItem(option);
        const item = itemDetails.find(i => i.Id === (option ? option.value : ''));
        if (item) {
            const qty      = parseFloat(modalData.Quantity) || 1;
            const rate     = parseFloat(item.MRP || item.PurchaseRate || 0);
            const gstrate  = parseFloat(item.GSTRate || 0);

            // Item's own discount from master; top-up to reach 52%
            const discPct       = parseFloat(item.DiscountPercentage || 0);
            const additionalPct = parseFloat(Math.max(0, TOTAL_DISC_PCT - discPct).toFixed(2));

            const discAmt       = (rate * discPct) / 100;
            const additionalAmt = (rate * additionalPct) / 100;
            const rateAfterDisc = rate - discAmt - additionalAmt;
            const amount        = (qty * rate).toFixed(2);

            setIsValid(qty <= item.balanceStock);
            const { taxable, cgst, sgst, igst, total } = calcGST(qty, rate, rateAfterDisc, gstrate, formData.GSTNumber);
            setModalData({
                ...modalData,
                ItemId: option.value, ItemName: option.label,
                Rate: rate.toFixed(2), GSTRate: gstrate.toFixed(2), HSNCode: item.HSNCode || '',
                DiscountPercentage: discPct.toFixed(2),
                DiscountAmount: (discAmt * qty).toFixed(2),
                AdditionalDiscountPercentage: additionalPct.toFixed(2),
                AdditionalDiscountAmount: (additionalAmt * qty).toFixed(2),
                RateAfterDiscount: rateAfterDisc.toFixed(2), Amount: amount,
                TaxableAmount: taxable, CGSTAmount: cgst, SGSTAmount: sgst, IGSTAmount: igst,
                TotalAmount: String(total), balanceStock: item.balanceStock || 0,
            });
        } else {
            setModalData({ ...emptyModal });
        }
    };

    /* ── Inline bar field change ── */
    const handleModalInputChange = (e) => {
        const { name, value } = e.target;
        const updated = { ...modalData, [name]: value };

        const rate    = parseFloat(name === 'Rate'     ? value : updated.Rate) || 0;
        const qty     = parseFloat(name === 'Quantity' ? value : updated.Quantity) || 0;
        const gstrate = parseFloat(updated.GSTRate) || 0;

        // Item's own discount (user-entered)
        const discPct = parseFloat(name === 'DiscountPercentage' ? value : updated.DiscountPercentage) || 0;

        // Top-up: whatever is needed to reach 52%. Always ≥ 0.
        const additionalPct = parseFloat(Math.max(0, TOTAL_DISC_PCT - discPct).toFixed(2));

        const discAmt       = (rate * discPct) / 100;
        const additionalAmt = (rate * additionalPct) / 100;
        const rateAfterDisc = rate - discAmt - additionalAmt;
        const amount        = (qty * rate).toFixed(2);

        setIsValid(qty <= updated.balanceStock);
        const { taxable, cgst, sgst, igst, total } = calcGST(qty, rate, rateAfterDisc, gstrate, formData.GSTNumber);
        setModalData({
            ...updated,
            AdditionalDiscountPercentage: String(additionalPct),
            DiscountAmount: (discAmt * qty).toFixed(2),
            AdditionalDiscountAmount: (additionalAmt * qty).toFixed(2),
            RateAfterDiscount: rateAfterDisc.toFixed(2),
            Amount: amount,
            TaxableAmount: taxable, CGSTAmount: cgst, SGSTAmount: sgst, IGSTAmount: igst,
            TotalAmount: String(total),
        });
    };

    /* ── Save item from bar ── */
    const saveModalData = () => {
        if (!modalData.ItemId) { setErrorMessageModel('Please select an item.'); return; }
        if (!modalData.Quantity || parseFloat(modalData.Quantity) <= 0) { setErrorMessageModel('Quantity must be > 0.'); return; }
        setErrorMessageModel('');
        const newItem = { ...modalData, FromBranchId: branchId, ToBranchId: selectedToBranch };
        const updated = [...invoiceDetails, newItem];
        setInvoiceDetails(updated);
        recalculateTotals(updated);
        setModalData(emptyModal);
        setSelectedItem(null);
        setIsValid(true);
    };

    /* ── Remove item ── */
    const removeRecord = (index) => setConfirmRemove(index);
    const confirmRemoveRecord = () => {
        const updated = [...invoiceDetails];
        updated.splice(confirmRemove, 1);
        setInvoiceDetails(updated);
        setConfirmRemove(null);
        recalculateTotals(updated);
    };

    /* ── Recalculate header totals ── */
    const recalculateTotals = (items) => {
        const totals = items.reduce((acc, item) => ({
            Quantity:      acc.Quantity      + parseFloat(item.Quantity || 0),
            TotalAmount:   acc.TotalAmount   + parseFloat(item.Amount || 0),
            DiscountAmount:acc.DiscountAmount + parseFloat(item.DiscountAmount || 0) + parseFloat(item.AdditionalDiscountAmount || 0),
            TaxableAmount: acc.TaxableAmount + parseFloat(item.TaxableAmount || 0),
            CGSTAmount:    acc.CGSTAmount    + parseFloat(item.CGSTAmount || 0),
            SGSTAmount:    acc.SGSTAmount    + parseFloat(item.SGSTAmount || 0),
            IGSTAmount:    acc.IGSTAmount    + parseFloat(item.IGSTAmount || 0),
            GrandTotal:    acc.GrandTotal    + parseFloat(item.TotalAmount || 0),
        }), { Quantity: 0, TotalAmount: 0, DiscountAmount: 0, TaxableAmount: 0, CGSTAmount: 0, SGSTAmount: 0, IGSTAmount: 0, GrandTotal: 0 });

        const grandTotalRaw = totals.GrandTotal;
        const roundOff      = Math.round(grandTotalRaw) - grandTotalRaw;
        const grandTotal    = Math.round(grandTotalRaw);
        const paid          = (parseFloat(formData.CashAmount) || 0) + (parseFloat(formData.CardAmount) || 0) + (parseFloat(formData.UPIAmount) || 0);

        setFormData(prev => ({
            ...prev,
            TotalQuantity:  totals.Quantity,
            TotalAmount:    parseFloat(totals.TotalAmount.toFixed(2)),
            DiscountAmount: parseFloat(totals.DiscountAmount.toFixed(2)),
            TaxableAmount:  parseFloat(totals.TaxableAmount.toFixed(2)),
            CGSTAmount:     parseFloat(totals.CGSTAmount.toFixed(2)),
            SGSTAmount:     parseFloat(totals.SGSTAmount.toFixed(2)),
            IGSTAmount:     parseFloat(totals.IGSTAmount.toFixed(2)),
            RoundOff:       parseFloat(roundOff.toFixed(2)),
            GrandTotal:     grandTotal,
            BalanceAmount:  parseFloat((grandTotal - paid).toFixed(2)),
        }));
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        const updated = { ...formData, [name]: type === 'checkbox' ? checked : value };
        setFormData(updated);
        if (['CashAmount', 'CardAmount', 'UPIAmount'].includes(name)) {
            const grandTotal = parseFloat(updated.GrandTotal) || 0;
            const paid = (parseFloat(updated.CashAmount) || 0) + (parseFloat(updated.CardAmount) || 0) + (parseFloat(updated.UPIAmount) || 0);
            setFormData(prev => ({ ...prev, [name]: value, BalanceAmount: parseFloat((grandTotal - paid).toFixed(2)) }));
        }
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        setIsLoading(true);
        if (!formData.CustomerName) { setErrorMessage('Customer Name is required!'); setIsLoading(false); return; }
        if (!selectedToBranch)      { setErrorMessage('To Branch is required!');      setIsLoading(false); return; }
        if (!selectedSalesMan)      { setErrorMessage('Sales Man is required!');       setIsLoading(false); return; }
        if (!invoiceDetails || invoiceDetails.length === 0) { setErrorMessage('Item Details are required!'); setIsLoading(false); return; }

        const payload = {
            InterCityStockTransfer: {
                ...formData,
                CompanyId: companyId, FromBranchId: branchId,
                ToBranchId: selectedToBranch, SalesManId: selectedSalesMan,
                CreatedBy: userId, IsActive: true,
            },
            InterCityStockTransferDetails: invoiceDetails,
        };

        postData('InterCityStockTransferApi', payload, token)
            .then((response) => {
                const newId = response?.kotId || response?.id || response?.Id;
                if (newId) handlePrint(newId);
                history.push('/InterCityStockTransferIndex');
            })
            .catch(err => {
                console.error('Error creating record:', err);
                setErrorMessage('Failed to save. Please try again.');
                setIsLoading(false);
            });
    };

    const handlePrint = async (printId) => {
        try {
            const response = await fetchData(
                'InterCityStockTransferApi',
                `/Print/${printId}/${branchId}`,
                token
            );
            generateInterCityPDF(response.response_data);
        } catch (error) {
            console.error('Error printing Inter City Stock Transfer:', error);
        }
    };

    const hasCreate = permissions.includes('Create Transaction');

    return (
        <Fragment>
            <div className="pg-container">
                <div className="pg-header">
                    <h5 className="pg-title">Inter City Stock Transfer – Create</h5>
                    <div className="pg-header-actions">
                        <Link to="/InterCityStockTransferIndex" className="pg-btn pg-btn-danger">
                            <CloseIcon /> Back to List
                        </Link>
                    </div>
                </div>

                {errorMessage && (
                    <div className="pg-alert pg-alert-error">
                        <WarningAmberIcon style={{ fontSize: 16, marginRight: 6 }} />{errorMessage}
                    </div>
                )}
                {successMessage && <div className="pg-alert pg-alert-success">{successMessage}</div>}

                <div className="pg-card">
                    {/* ── Row 1: Bill info ── */}
                    <div className="pg-hpanel">
                        <div className="pg-hcell" style={{ flex: '1 1 0' }}>
                            <span className="pg-hcell-label">Bill Date</span>
                            <input className="pg-hcell-input" type="date" name="BillDate"
                                value={formData.BillDate || ''} onChange={handleChange} />
                        </div>
                        <div className="pg-hcell" style={{ flex: '1 1 0' }}>
                            <span className="pg-hcell-label">Bill Prefix</span>
                            <input className="pg-hcell-input pg-hcell-readonly" type="text"
                                value={formData.BillPrefix || ''} readOnly />
                        </div>
                        <div className="pg-hcell" style={{ flex: '1 1 0' }}>
                            <span className="pg-hcell-label">Bill Number</span>
                            <input className="pg-hcell-input pg-hcell-readonly" type="text"
                                value={formData.BillNumber || ''} readOnly />
                        </div>
                        <div className="pg-hcell" style={{ flex: '2 1 0' }}>
                            <span className="pg-hcell-label">To Branch <span style={{ color: 'red' }}>*</span></span>
                            <select className="pg-hcell-select" value={selectedToBranch}
                                onChange={e => setSelectedToBranch(e.target.value)}>
                                <option value="">Select To Branch</option>
                                {dropdownBranchOptions.map(b => <option key={b.Id} value={b.Id}>{b.Name}</option>)}
                            </select>
                        </div>
                        <div className="pg-hcell" style={{ flex: '2 1 0' }}>
                            <span className="pg-hcell-label">Sales Man <span style={{ color: 'red' }}>*</span></span>
                            <select className="pg-hcell-select" value={selectedSalesMan}
                                onChange={e => setSelectedSalesMan(e.target.value)}>
                                <option value="">Select Sales Man</option>
                                {dropdownSalesManOptions.map(s => <option key={s.Id} value={s.Id}>{s.Name}</option>)}
                            </select>
                        </div>
                    </div>

                    {/* ── Row 2: Customer info ── */}
                    <div className="pg-hpanel" style={{ marginTop: 8 }}>
                        <div className="pg-hcell" style={{ flex: '3 1 0' }}>
                            <span className="pg-hcell-label">Customer Name <span style={{ color: 'red' }}>*</span></span>
                            <input className="pg-hcell-input" type="text" name="CustomerName"
                                value={formData.CustomerName || ''} onChange={handleChange} placeholder="Customer Name" />
                        </div>
                        <div className="pg-hcell" style={{ flex: '3 1 0' }}>
                            <span className="pg-hcell-label">Customer Address</span>
                            <input className="pg-hcell-input" type="text" name="CustomerAddress"
                                value={formData.CustomerAddress || ''} onChange={handleChange} placeholder="Address" />
                        </div>
                        <div className="pg-hcell" style={{ flex: '2 1 0' }}>
                            <span className="pg-hcell-label">GST Number</span>
                            <input className="pg-hcell-input" type="text" name="GSTNumber"
                                value={formData.GSTNumber || ''} onChange={handleChange} placeholder="GST Number" />
                        </div>
                        <div className="pg-hcell" style={{ flex: '2 1 0' }}>
                            <span className="pg-hcell-label">Contact Number</span>
                            <input className="pg-hcell-input" type="text" name="ContactNumber"
                                value={formData.ContactNumber || ''} onChange={handleChange} placeholder="Contact Number" />
                        </div>
                    </div>

                    {/* ── Error from item bar ── */}
                    {errorMessageModel && (
                        <div className="pg-alert pg-alert-error" style={{ marginTop: 8 }}>
                            {errorMessageModel}
                        </div>
                    )}

                    {/* ── Item Add Bar (inline, same as Sale) ── */}
                    <div className="pg-item-bar" style={{ marginTop: 12 }}>
                        <div className="pg-field" style={{ flex: '3 1 200px' }}>
                            <label className="pg-label">Item Name</label>
                            <div style={{ marginTop: 6 }}>
                                <Select
                                    value={selectedItem}
                                    onChange={handleItemSelect}
                                    options={itemsOptions}
                                    placeholder="Search items…"
                                    isSearchable
                                    menuPortalTarget={document.body}
                                    menuPosition="fixed"
                                    styles={{
                                        control: b => ({ ...b, minHeight: 31, height: 31, fontSize: 12.5, borderColor: 'rgba(200,212,224,0.7)', borderRadius: 6 }),
                                        valueContainer: b => ({ ...b, padding: '0 10px' }),
                                        indicatorsContainer: b => ({ ...b, height: 31 }),
                                        menuPortal: b => ({ ...b, zIndex: 9999 }),
                                    }}
                                />
                            </div>
                        </div>
                        <div className="pg-field narrow">
                            <label className="pg-label">Quantity</label>
                            <input className={`pg-input text-right${!isValid ? ' cash-negative' : ''}`}
                                type="number" name="Quantity" placeholder="Qty"
                                value={modalData.Quantity} onChange={handleModalInputChange} min={1} />
                            {!isValid && <span style={{ color: '#dc2626', fontSize: 11 }}>Exceeds stock</span>}
                        </div>
                        <div className="pg-field narrow">
                            <label className="pg-label">Rate</label>
                            <input className="pg-input text-right" type="number" name="Rate"
                                value={modalData.Rate} onChange={handleModalInputChange} />
                        </div>
                        <div className="pg-field narrow">
                            <label className="pg-label">Disc %</label>
                            <input className="pg-input text-right" type="number" name="DiscountPercentage"
                                value={modalData.DiscountPercentage} onChange={handleModalInputChange} />
                        </div>
                        <div className="pg-field narrow">
                            <label className="pg-label" title="Auto top-up to 52%">Addt'l Disc %</label>
                            <input className="pg-input text-right pg-hcell-readonly" type="text"
                                value={modalData.AdditionalDiscountPercentage} readOnly
                                style={{ background: '#f0fdf4', color: '#15803d', fontWeight: 600 }} />
                        </div>
                        <div className="pg-field narrow">
                            <label className="pg-label">Disc Amt</label>
                            <input className="pg-input text-right" type="text"
                                value={modalData.DiscountAmount} readOnly />
                        </div>
                        <div className="pg-field narrow">
                            <label className="pg-label">GST %</label>
                            <input className="pg-input text-right" type="text"
                                value={modalData.GSTRate} readOnly />
                        </div>
                        {modalData.balanceStock > 0 && (
                            <div style={{ display: 'flex', alignItems: 'flex-end', fontSize: 11, color: '#64748b', paddingBottom: 4 }}>
                                Stock: {modalData.balanceStock}
                            </div>
                        )}
                        <div style={{ display: 'flex', alignItems: 'flex-end' }}>
                            <button className="pg-btn pg-btn-success" onClick={saveModalData}>
                                <AddIcon /> Add
                            </button>
                        </div>
                    </div>

                    {/* ── Invoice Detail Table ── */}
                    <div className="pg-invoice-table-wrap" style={{ marginTop: 12 }}>
                        <table className="pg-invoice-table">
                            <thead>
                                <tr>
                                    <th>#</th>
                                    <th>Item</th>
                                    <th className="right">Qty</th>
                                    <th className="right">Rate</th>
                                    <th className="right">Disc %</th>
                                    <th className="right">Disc Amt</th>
                                    <th className="right">Addt'l Disc %</th>
                                    <th className="right">Addt'l Disc Amt</th>
                                    <th className="right">Rate After Disc</th>
                                    <th className="right">HSN</th>
                                    <th className="right">GST %</th>
                                    <th className="right">Taxable</th>
                                    <th className="right">CGST</th>
                                    <th className="right">SGST</th>
                                    <th className="right">IGST</th>
                                    <th className="right">Total</th>
                                    <th></th>
                                </tr>
                            </thead>
                            <tbody>
                                {invoiceDetails.length === 0
                                    ? <tr><td colSpan={17} style={{ textAlign: 'center', color: '#94a3b8', padding: 14 }}>No items added yet.</td></tr>
                                    : invoiceDetails.map((item, index) => (
                                        <tr key={index}>
                                            <td style={{ textAlign: 'center', width: 36 }}>{index + 1}</td>
                                            <td>{item.ItemName}</td>
                                            <td className="right">{item.Quantity}</td>
                                            <td className="right">{item.Rate}</td>
                                            <td className="right">{item.DiscountPercentage}</td>
                                            <td className="right">{item.DiscountAmount}</td>
                                            <td className="right" style={{ color: '#15803d', fontWeight: 600 }}>{item.AdditionalDiscountPercentage}</td>
                                            <td className="right" style={{ color: '#15803d' }}>{item.AdditionalDiscountAmount}</td>
                                            <td className="right">{item.RateAfterDiscount}</td>
                                            <td className="right">{item.HSNCode}</td>
                                            <td className="right">{item.GSTRate}</td>
                                            <td className="right">{item.TaxableAmount}</td>
                                            <td className="right">{item.CGSTAmount}</td>
                                            <td className="right">{item.SGSTAmount}</td>
                                            <td className="right">{item.IGSTAmount}</td>
                                            <td className="right" style={{ fontWeight: 700 }}>{item.TotalAmount}</td>
                                            <td style={{ textAlign: 'center' }}>
                                                <IconButton size="small" onClick={() => removeRecord(index)} title="Remove">
                                                    <DeleteIcon style={{ fontSize: 16, color: '#dc2626' }} />
                                                </IconButton>
                                            </td>
                                        </tr>
                                    ))
                                }
                            </tbody>
                        </table>
                    </div>

                    {/* ── Totals Panel ── */}
                    <div className="pg-totals-panel">
                        <div className="pg-totals-left">
                            <div className="pg-totals-summary">
                                <div className="pg-tsum-row"><span className="pg-tsum-label">Total Amount</span><span className="pg-tsum-value">{(formData.TotalAmount || 0).toFixed(2)}</span></div>
                                <div className="pg-tsum-row"><span className="pg-tsum-label">Discount</span><span className="pg-tsum-value">{(formData.DiscountAmount || 0).toFixed(2)}</span></div>
                                <div className="pg-tsum-row"><span className="pg-tsum-label">Net Amount</span><span className="pg-tsum-value">{(formData.TaxableAmount || 0).toFixed(2)}</span></div>
                                <div className="pg-tsum-row"><span className="pg-tsum-label">Total Qty</span><span className="pg-tsum-value">{formData.TotalQuantity || 0}</span></div>
                            </div>
                        </div>
                        <div className="pg-totals-divider" />
                        <div className="pg-totals-right">
                            <div className="pg-totals-remarks">
                                <label className="pg-tpay-label">Remarks</label>
                                <textarea className="pg-remarks-textarea" name="Remarks"
                                    placeholder="Remarks" value={formData.Remarks || ''} onChange={handleChange} />
                            </div>
                            <div className="pg-totals-gst-row">
                                <div className="pg-tgst-cell"><span className="pg-tgst-label">CGST</span><span className="pg-tgst-value">{(formData.CGSTAmount || 0).toFixed(2)}</span></div>
                                <div className="pg-tgst-cell"><span className="pg-tgst-label">SGST</span><span className="pg-tgst-value">{(formData.SGSTAmount || 0).toFixed(2)}</span></div>
                                <div className="pg-tgst-cell"><span className="pg-tgst-label">IGST</span><span className="pg-tgst-value">{(formData.IGSTAmount || 0).toFixed(2)}</span></div>
                                <div className="pg-tgst-cell"><span className="pg-tgst-label">Round Off</span><span className="pg-tgst-value">{(formData.RoundOff || 0).toFixed(2)}</span></div>
                            </div>
                            <div className="pg-grand-total-bar">
                                <span className="pg-gt-label">Grand Total</span>
                                <span className="pg-gt-value">{(formData.GrandTotal || 0).toFixed(2)}</span>
                            </div>
                            <div className="pg-totals-pay-row">
                                <div className="pg-tpay-cell"><label className="pg-tpay-label">Cash</label><input className="pg-tpay-input" type="number" name="CashAmount" value={formData.CashAmount || 0} onChange={handleChange} /></div>
                                <div className="pg-tpay-cell"><label className="pg-tpay-label">Card</label><input className="pg-tpay-input" type="number" name="CardAmount" value={formData.CardAmount || 0} onChange={handleChange} /></div>
                                <div className="pg-tpay-cell"><label className="pg-tpay-label">UPI / RTGS / Other</label><input className="pg-tpay-input" type="number" name="UPIAmount" value={formData.UPIAmount || 0} onChange={handleChange} /></div>
                                <div className="pg-tpay-cell"><label className="pg-tpay-label">Balance</label><input className="pg-tpay-input pg-hcell-readonly" type="text" value={(formData.BalanceAmount || 0).toFixed(2)} readOnly /></div>
                            </div>
                        </div>
                    </div>

                    {/* ── Submit ── */}
                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
                        <Link to="/InterCityStockTransferIndex" className="pg-btn pg-btn-danger">
                            <CloseIcon style={{ fontSize: 15 }} /> Cancel
                        </Link>
                        <button type="button" className="pg-btn pg-btn-success" onClick={handleSubmit} disabled={isLoading}>
                            {isLoading ? <CircularProgress size={14} color="inherit" style={{ marginRight: 6 }} /> : <DoneIcon style={{ fontSize: 15 }} />}
                            Save
                        </button>
                    </div>
                </div>

                {/* ── Remove Confirm Modal ── */}
                {confirmRemove !== null && (
                    <div className="pg-modal-backdrop" onClick={() => setConfirmRemove(null)}>
                        <div className="pg-modal-card" onClick={e => e.stopPropagation()}>
                            <div className="pg-modal-icon-wrap">
                                <WarningAmberIcon className="pg-modal-icon" />
                            </div>
                            <h6 className="pg-modal-title">Remove Item?</h6>
                            <p className="pg-modal-body">This item will be removed from the invoice.</p>
                            <div className="pg-modal-actions">
                                <button className="pg-modal-btn pg-modal-btn-cancel" onClick={() => setConfirmRemove(null)}>Cancel</button>
                                <button className="pg-modal-btn pg-modal-btn-confirm" onClick={confirmRemoveRecord}>
                                    <DeleteIcon style={{ fontSize: 15 }} /> Yes, Remove
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                <div className="pg-watermark"><CopyrightIcon style={{ fontSize: 12 }} /><b>EFFTABS SOLUTIONS LLP</b></div>
            </div>
        </Fragment>
    );
};

export default InterCityStockTransferCreate;
